import Component from "../component";
import { loadScript } from "../../utils/load_script";
import GeolocateComponent, { GeolocateComponentEvents } from "./geolocate";
import { GenericPosition } from "../../services/geolocation";
import { getLocale, getLocaleLang } from "../../helpers/locale";
import { merge, replace } from "../../utils/utils";
import { getConfig } from "../../configuration/config";
import selectors from "configuration/selectors.config";
import {WoosmapApiClient} from "../../services/woosmap_api";

export interface MultiSearchLocation {
    name?: string;
    publicId?: string;
    location?: woosmap.map.LatLngLiteral;
}
export interface ApiOptions {
    fallbackBreakpoint?: number | boolean,
    key: string,
    minInputLength?: number,
    params?: unknown

}
export interface MultiSearchConfig {
    debounceTime?: number,
    localities?: ApiOptions,
    store?: ApiOptions
}

export interface IMultiSearchComponent {
    inputID: string;
    woosmapPublicKey: string;
    searchOptions: MultiSearchConfig;
    selectedLocality?: MultiSearchLocation;
    featuresBtn: string[];
    searchText?: string;
}

export enum SearchComponentEvents {
    SELECTED_LOCALITY = "selected_locality",
    SEARCH_CLEAR = "search_clear",
    SELECTED_STORE = "selected_store",
}

export default class MultiSearchComponent extends Component<IMultiSearchComponent> {
    private multiSearch!: window.woosmap.multisearch;

    init(): void {
        const $multiSearchInputContainer = document.createElement("div") as HTMLDivElement;
        $multiSearchInputContainer.className = 'multisearch__container';
        this.$element = document.createElement("input") as HTMLInputElement;
        this.$element.className = "search__input";
        this.$element.setAttribute("placeholder", getLocale().search.placeholder);
        this.$element.setAttribute("aria-label", getLocale().search.placeholder);
        this.$element.setAttribute("aria-autocomplete", "list");
        this.$element.setAttribute("autocomplete", "off");
        this.$element.id = this.state.inputID;
        $multiSearchInputContainer.appendChild(this.$element);
        this.$target.appendChild($multiSearchInputContainer);
    }

    render(): void {
        if (this.state && this.$element) {
            loadScript({
                url: getConfig().urls.multiSearchJS,
                params: { key: this.state.woosmapPublicKey }
            })
                .then(() => this.initSearchView())
                .catch((error) => {
                    console.error(
                        replace(getLocale().errors.loadingLibrary, { 'library': "Woosmap MultiSearch JS" }),
                        error
                    );
                });
        }
    }

    initSearchView(): void {
        const searchOptions = merge({ ...this.state.searchOptions }, { language: getLocaleLang().toLowerCase() })
        this.multiSearch = window.woosmap.multisearch(searchOptions);
        const $inputContainer = (document.getElementById(this.state.inputID) as HTMLElement).parentElement as HTMLElement;
        const $inputElement = document.getElementById(this.state.inputID) as HTMLInputElement;
        $inputElement?.addEventListener("input", ()=> this.handleAutoComplete($inputElement));
        $inputContainer.appendChild($inputElement);
        this.state.featuresBtn.forEach((feature: string) => {
            if (feature === 'search') {
                this.manageSearchButton($inputContainer);
            }
            if (feature === 'clear') {
                this.manageClearButton($inputContainer);
            }
            if (feature === 'geolocate') {
                this.manageGeolocateButton($inputContainer);
            }
        });
        this.selectLocality();
    }
    handleAutoComplete($inputElement: HTMLInputElement): void {
        const input = $inputElement.value;
        input.replace('"', '\\"').replace(/^\s+|\s+$/g, "");
        if (input !== "") {
            this.multiSearch.autocompleteMulti(input).then(
                (results: any) => this.displaySuggestions(results),
                (error: any) => console.error(`Error autocomplete localities: ${error}`),
            );
        }
    }
    displaySuggestions(results: any) {
        const $inputElement = document.getElementById(selectors.searchInputID) as HTMLInputElement;
        const $suggestionsList = document.getElementById(selectors.suggestionListID) as HTMLDivElement;
        $suggestionsList.className = "suggestions-list";
        if ($inputElement && $suggestionsList) {
            $suggestionsList.innerHTML = "";
            if (results.length > 0) {
                results.forEach((result: any) => {
                    const li = document.createElement("li");
                    li.className = `${result.api}-api`;
                    li.innerHTML = this.formatPredictionList(result) ?? "";
                    li.addEventListener("click", () => {
                        $inputElement.value = result.description ?? "";
                        $suggestionsList.style.display = "none";
                        if(result.api === 'store'){
                            const api = new WoosmapApiClient({apiKey: getConfig().map.woosmapPublicKey});
                            api.stores
                                .searchStores({query: `idstore:="${result.id}"`})
                                .then((response) => {
                                    const details = response?.features[0]
                                    const searchLocation: MultiSearchLocation = {
                                        name: details.properties.name,
                                        publicId: details.properties.store_id,
                                        location: {lat: details.geometry.coordinates[1], lng: details.geometry.coordinates[0]}
                                    }
                                    this.setState({ selectedLocality: searchLocation }, true)
                                    this.emit(SearchComponentEvents.SELECTED_STORE, details);
                                })
                                .catch((exception) => {
                                    console.error(exception);
                                });
                        }
                        else{
                            this.multiSearch
                            .detailsMulti({ id: result.id, api: result.api })
                            .then((details: any) => {
                                const searchLocation: MultiSearchLocation = {
                                    name: details.formatted_address || details.name,
                                    publicId: details.id,
                                    location: details.geometry.location
                                }
                                this.setState({ selectedLocality: searchLocation }, true)
                                this.emit(SearchComponentEvents.SELECTED_LOCALITY, searchLocation);
                            });
                        }
                        
                    });
                    $suggestionsList.appendChild(li);
                });
                $suggestionsList.style.display = "block";
                this.$element.parentNode?.appendChild($suggestionsList);
            }
        }
    }
    formatPredictionList(result: any): string {
        const predictionClass = "no-viewpoint";
        const formatted_name = result.highlight;
        let html = "";
        html += `<div class="api-icon"></div><div class="prediction ${predictionClass}">${formatted_name}</div>`;
        return html;
    }
    selectLocality(): void {
        if (this.state.selectedLocality) {
            this.setLocality(this.state.selectedLocality.name || '');
            this.emit(SearchComponentEvents.SELECTED_LOCALITY, this.state.selectedLocality);
        }
    }

    manageSearchButton($inputContainer: HTMLElement): void {
        const $searchBtn = document.createElement("div");
        $searchBtn.className = "search__searchBtn";
        $searchBtn.innerHTML = `<button type="button" aria-label="${getLocale().search.submitLabel}"></button>`
        $searchBtn.addEventListener("click", () => {
            if (this.state.selectedLocality) {
                this.emit(SearchComponentEvents.SELECTED_LOCALITY, this.state.selectedLocality);
            } else {
                this.$element.focus();
            }
        });
        $inputContainer.appendChild($searchBtn);
    }

    handleClearBtnClick = ($clearBtn: HTMLElement, $inputContainer: HTMLElement) => {
        const $emptyButton = $inputContainer.querySelector(".localities-empty-button") as HTMLDivElement
        const $suggestionsList = document.getElementById(selectors.suggestionListID) as HTMLDivElement;
        if ($emptyButton) {
            $emptyButton.click();
            $clearBtn.style.display = 'none';
        } else {
            (this.$element as HTMLInputElement).value = "";
            $clearBtn.style.display = 'none';
            this.$element.focus();
        }
        $suggestionsList.innerHTML = "";
        this.emit(SearchComponentEvents.SEARCH_CLEAR);
    }

    handleInputChange = ($clearBtn: HTMLElement) => {
        if ((this.$element as HTMLInputElement).value.length === 0) {
            $clearBtn.style.display = 'none';
        } else {
            $clearBtn.style.display = 'block';
        }
    }

    manageClearButton($inputContainer: HTMLElement): void {
        const $clearBtn = document.createElement("div");
        $clearBtn.className = "search__clearBtn";
        $clearBtn.innerHTML = `<button type="button" aria-label="${getLocale().search.clearLabel}"></button>`
        $clearBtn.addEventListener("click", () => this.handleClearBtnClick($clearBtn, $inputContainer));
        ['input', 'locality_changed'].forEach(evt => this.$element.addEventListener(evt, () => this.handleInputChange($clearBtn)));
        $clearBtn.style.display = 'none';
        $inputContainer.appendChild($clearBtn);
    }
    setLocality(name: string): void {
        (this.$element as HTMLInputElement).value = name;
        (this.$element as HTMLInputElement).dispatchEvent(new Event('locality_changed'))
    }

    manageGeolocateButton($inputContainer: HTMLElement): void {
        const geolocateComponent = new GeolocateComponent({
            $target: $inputContainer,
            initialState: {},
        });
        geolocateComponent.on(GeolocateComponentEvents.GEOLOCATION_FOUND, (geolocation: GenericPosition) => {
            if (geolocation) {
                const searchLocation: MultiSearchLocation = {
                    name: geolocation.name,
                    location: {
                        lat: geolocation.latitude,
                        lng: geolocation.longitude,
                    }
                }
                this.setLocality(getLocale().search.yourLocation);
                this.setState({ selectedLocality: searchLocation }, true)
                this.emit(SearchComponentEvents.SELECTED_LOCALITY, searchLocation);
            }
        })
    }
    getLocality(): MultiSearchLocation | undefined {
        if (this.state) {
            return this.state.selectedLocality
        }
    }
}
